// frontend/src/components/profile-components/DailyMessageBanner.js

import React, { useState, useEffect } from 'react'; 
import { Link } from 'react-router-dom'; 
import { MessageSquare, AlertTriangle, Loader2 } from 'lucide-react';

import { getDailyMessage } from '../../services/profileService'; 
import { getCurrentPersianDate as getPersianDate } from '../../utils/dateUtils'; 

export function DailyMessageBanner() {
    
    const [messageData, setMessageData] = useState(null); 
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        const fetchMessage = async () => {
            try {
                const data = await getDailyMessage();
                setMessageData(data); 
            } catch (error) {
                console.error("Error fetching daily message:", error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchMessage();
    }, []); 

    // Loading state
    if (isLoading) {
        return (
             <div className="p-3 bg-gray-100 text-gray-500 rounded-xl text-[11px] text-center flex items-center justify-center rtl">
                 <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                 در حال بارگذاری پیام روزانه...
             </div>
        );
    }
    
    // Error state
    if (isError) {
        return (
            <div className="p-4 bg-red-100 border-r-4 border-red-500 text-red-700 rounded-xl text-[11px] text-justify flex items-center rtl text-right">
                <AlertTriangle className="w-4 h-4 ml-2 flex-shrink-0" />
                <span className="font-medium">خطا در واکشی پیام روزانه.</span> لطفاً بعداً تلاش کنید.
            </div>
        );
    }

    const { title, message, product_slug, book_slug, link_text } = messageData || {};

    // If there is no content at all, render nothing
    if (!title && !message && !product_slug && !book_slug) {
        return null;
    }
    
    // Display date in "day month year" format (raw output is already in that order)
    const rawDate = getPersianDate(); // e.g., "۲۲ خرداد ۱۴۰۴"
    const displayDate = rawDate; // just display as is

    return (
        <div 
            className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm text-right" 
            role="alert"
        >
            <div className="flex justify-end items-center mb-3 text-[11px] font-medium text-gray-500 border-b border-gray-100 pb-2">
                <span className="text-sky-400 font-extrabold text-[11px]">{displayDate}</span>
            </div>

            <div className="flex flex-col">
                <div className="flex items-center mb-2">
                    <MessageSquare className="w-5 h-5 ml-2 text-purple-700 flex-shrink-0" />
                    {title && <p className="font-light text-[12pt] text-gray-800">{title}</p>} 
                </div>
                
                {message && (
                    <p 
                        className="text-[10pt] font-medium text-justify text-gray-500 leading-relaxed mb-4 max-h-64 overflow-y-auto"
                    >
                        {message}
                    </p>
                )}
                
                {link_text && product_slug && (
                    <Link 
                        to={`/products/${product_slug}`} 
                        className="mt-3 text-xs font-medium text-purple-700 hover:text-purple-900 transition self-start"
                    >
                        {link_text} &larr;
                    </Link>
                )}
                
                {link_text && book_slug && !product_slug && (
                    <Link 
                        to={`/books/${book_slug}`}
                        className="mt-3 text-xs font-medium text-purple-700 hover:text-purple-900 transition self-start"
                    >
                        {link_text} &larr;
                    </Link>
                )}
            </div>
        </div>
    );
}